import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  Menu, 
  X, 
  ChevronLeft,
  ChevronRight,
  Home,
  Car,
  Package,
  HelpCircle,
  LogOut,
  Crown,
  Calendar,
  DollarSign,
  Settings,
  Sparkles,
  MessageCircle,
  Image,
  Video,
  Wrench,
  Clock,
  ShoppingBag,
  Globe,
  Lock,
  Key,
  Gem,
  RefreshCw
} from 'lucide-react';
import { logoutUser, fetchUserProfile } from '../store/userSlice';
import { useSidebar } from '../contexts/SidebarContext';
import LoginModal from './LoginModal';
import LanguageSwitcher from './LanguageSwitcher';

export default function Sidebar() {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [comingSoonText, setComingSoonText] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // 登录弹窗状态
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 获取用户信息
  const { user, isLoggedIn } = useSelector((state) => state.user);
  
  // 处理登录需求
  const handleLoginRequired = () => {
    setShowLoginModal(true);
  };
  
  // 处理登录成功
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
  };
  
  // 处理关闭登录弹窗
  const handleCloseModal = () => {
    setShowLoginModal(false);
  };

  // 处理刷新用户信息
  const handleRefreshUserInfo = async () => {
    if (!isLoggedIn || isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await dispatch(fetchUserProfile()).unwrap();
      // 显示成功提示（可选）
      console.log('用户信息刷新成功');
    } catch (error) {
      console.error('刷新用户信息失败:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // 监听语言变化，强制重新渲染
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
    };

    // 监听i18n语言变化事件
    i18n.on('languageChanged', handleLanguageChange);
    
    // 监听自定义语言变化事件
    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // AI功能模块
  const aiFeatures = [
    {
      name: t('sidebar.aiFeatures.aiChat'),
      href: '/ai-chat/sidebar',
      icon: MessageCircle,
      requireAuth: true,
      comingSoon: true
    },
    {
      name: t('sidebar.aiFeatures.aiImage'),
      href: '/ai-image/sidebar',
      icon: Image,
      requireAuth: true,
      comingSoon: true
    },
    {
      name: t('sidebar.aiFeatures.aiVideo'),
      href: '#',
      icon: Video,
      requireAuth: true,
      comingSoon: true
    },
    { 
      name: t('sidebar.aiFeatures.aiFleet'), 
      href: '/ai-fleet', 
      icon: Car,
      requireAuth: true 
    },
    {
      name: t('sidebar.aiFeatures.aiToolbox'),
      href: '/ai-toolbox/sidebar',
      icon: Wrench,
      requireAuth: true
    }
  ];

  // 用户服务模块
  const userServices = [
    {
      name: t('sidebar.userServices.settings'),
      href: '/settings',
      icon: Settings,
      requireAuth: true
    },
    { 
      name: t('sidebar.userServices.support'), 
      href: '/support-sidebar',
      icon: HelpCircle,
      requireAuth: false 
    }
  ];

  // 检查当前路径是否匹配
  const isActiveRoute = (href) => {
    if (href === '#') return false;
    
    if (href === '/orders-sidebar') {
      return location.pathname === '/orders-sidebar' || location.pathname === '/orders';
    }
    if (href === '/support-sidebar') {
      return location.pathname === '/support-sidebar' || location.pathname === '/support';
    }
    return location.pathname.startsWith(href);
  };

  // 处理登出
  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  // 处理Coming Soon点击
  const handleComingSoonClick = (itemName) => {
    setComingSoonText(t('sidebar.status.comingSoonMessage', { feature: itemName }));
    setShowComingSoon(true);
    setTimeout(() => {
      setShowComingSoon(false);
    }, 3000);
  };

  // 渲染导航项
  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = isActiveRoute(item.href);
    
    if (item.comingSoon) {
      return (
        <li key={item.name}>
          <button
            onClick={() => handleComingSoonClick(item.name)}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-300 group text-gray-400 hover:bg-gray-50 hover:text-gray-500 ${
              isCollapsed ? 'justify-center' : ''
            }`}
            title={isCollapsed ? `${item.name} (${t('sidebar.status.comingSoon')})` : ''}
          >
            <div className="relative">
              <Icon className="w-5 h-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
              {!isCollapsed && (
                <Clock className="w-3 h-3 text-orange-500 absolute -top-1 -right-1" />
              )}
            </div>
            {!isCollapsed && (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm">{item.name}</span>
                <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-medium">
                  {t('sidebar.status.soon')}
                </span>
              </div>
            )}
          </button>
        </li>
      );
    }
    
    return (
      <li key={item.name}>
        <Link
          to={item.href}
          className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-300 group ${
            isActive
              ? 'bg-orange-100 text-orange-600 font-semibold'
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
          } ${isCollapsed ? 'justify-center' : ''}`}
          title={isCollapsed ? item.name : ''}
        >
          <Icon className={`w-5 h-5 flex-shrink-0 ${
            isActive ? 'text-orange-600' : 'text-gray-500 group-hover:text-gray-700'
          }`} />
          {!isCollapsed && (
            <span className="text-sm">{item.name}</span>
          )}
        </Link>
      </li>
    );
  };

  return (
    <>
      <aside className={`fixed left-0 top-0 h-full bg-white border-r border-gray-200 shadow-lg transition-all duration-300 z-40 flex flex-col ${
        isCollapsed ? 'w-20' : 'w-64'
      }`}>
        {/* 顶部Logo区域 */}
        <div className="bg-orange-500 flex-shrink-0">
          {isCollapsed ? (
            // 收缩状态：Logo和按钮分两行
            <>
              <div className="h-16 flex items-center justify-center">
                <Link 
                  to="/"
                  className="flex items-center hover:opacity-80 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-md">
                    <img 
                      src="/images/logo.jpg" 
                      alt="AI Monkey Logo" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <span className="text-2xl hidden">🐵</span>
                  </div>
                </Link>
              </div>
              <div className="pb-3 flex justify-center">
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-300"
                >
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>
              </div>
            </>
          ) : (
            // 展开状态：Logo和按钮同一行
            <div className="h-16 flex items-center justify-between px-4">
              <Link 
                to="/"
                className="flex items-center hover:opacity-80 transition-all duration-300"
              >
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-md">
                  <img 
                    src="/images/logo.jpg" 
                    alt="AI Monkey Logo" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span className="text-2xl hidden">🐵</span>
                </div>
                <div className="ml-3">
                  <h1 className="text-xl font-bold text-white">{t('sidebar.brandName')}</h1>
                  <p className="text-xs text-white/80">{t('sidebar.brandSlogan')}</p>
                </div>
              </Link>
              
              <button
                onClick={toggleSidebar}
                className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-300"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
        </div>

        {/* 导航菜单 - 占据剩余空间 */}
        <nav className="flex-1 overflow-y-auto py-4">
          {/* 导航菜单项 */}
          <div className="space-y-1">
            <ul className="space-y-1 px-3">
              {aiFeatures.filter(item => !item.requireAuth || isLoggedIn).map(renderNavItem)}
            </ul>
            
            {/* AI工具箱和设置之间的分割线 */}
            <div className="mx-3 my-4">
              <div className="border-t border-gray-200"></div>
            </div>
            
            <ul className="space-y-1 px-3">
              {userServices.filter(item => !item.requireAuth || isLoggedIn).map(renderNavItem)}
            </ul>
          </div>

          {/* 语言切换器 - 在用户服务模块下方 */}
          <div className="mt-0">
            <ul className="space-y-1 px-3">
              <li>
                <div className={`flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-300 group text-gray-700 hover:bg-gray-100 hover:text-gray-900 ${
                  isCollapsed ? 'justify-center' : ''
                }`}>
                  <Globe className="w-5 h-5 flex-shrink-0 text-gray-500 group-hover:text-gray-700" />
                  {!isCollapsed && (
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-sm">{t('common.language')}</span>
                      <LanguageSwitcher className="sidebar-inline-language-switcher" />
                    </div>
                  )}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {t('common.language')}
                      </div>
                    </div>
                  )}
                </div>
                {/* 收缩状态下的语言切换器 */}
                {isCollapsed && (
                  <div className="mt-1 flex justify-center">
                    <LanguageSwitcher className="sidebar-language-switcher collapsed" />
                  </div>
                )}
              </li>
            </ul>
          </div>
        </nav>

        {/* 底部整合模块 */}
        {isLoggedIn && user && (
          <div className="flex-shrink-0">
            {/* 充值升级按钮 - 醒目的渐变设计 */}
            <div className={`${isCollapsed ? 'px-2' : 'px-4'} pb-3`}>
              <button
                onClick={() => navigate('/shop')}
                className={`w-full relative overflow-hidden group ${
                  isCollapsed ? 'p-3' : 'px-5 py-3'
                }`}
              >
                {/* 渐变背景 */}
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-orange-600 to-red-500 rounded-xl"></div>
                
                {/* 闪光效果 */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-xl"></div>
                
                {/* 内容 */}
                <div className="relative flex items-center justify-center gap-2">
                  <Sparkles className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-white`} />
                  {!isCollapsed && (
                    <>
                      <span className="text-white font-semibold text-sm">{t('sidebar.actions.upgrade')}</span>
                      <Crown className="w-4 h-4 text-yellow-300" />
                    </>
                  )}
                </div>
              </button>
            </div>
            
            {/* 用户信息和设置 - 优雅的卡片设计 */}
            <div className={`border-t border-gray-200 bg-gradient-to-t from-gray-50 to-white ${
              isCollapsed ? 'p-2' : 'p-4'
            }`}>
              {/* 用户信息卡片 - 点击进入设置 */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => navigate('/settings')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate('/settings');
                  }
                }}
                className={`w-full bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 ${
                  isCollapsed ? 'p-2' : 'p-3'
                } hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-200 focus:ring-offset-2 focus:ring-offset-white`}
              >
                {/* 用户信息 */}
                <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
                  {/* 用户头像 */}
                  <div className="relative">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center shadow-sm">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    {/* 在线状态指示器 */}
                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                    {/* 收缩状态下的钻石数量指示器 */}
                    {isCollapsed && user.diamond_count > 0 && (
                      <div className="absolute -top-1 -left-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                        <Gem className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </div>
                  
                  {/* 用户信息文字 */}
                  {!isCollapsed && (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {user.name || user.email || t('sidebar.user.user')}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {(() => {
                          const uid = user.uid || user.id || user.email || '';
                          if (uid.length > 16) {
                            return `${uid.substring(0, 6)}...${uid.substring(uid.length - 6)}`;
                          }
                          return uid;
                        })()}
                      </p>
                      {/* 钻石数量显示 */}
                      <div className="flex items-center gap-1 mt-1">
                        <Gem className="w-3 h-3 text-purple-500" />
                        <span className="text-xs text-purple-600 font-medium">
                          {user.diamond_count || 0}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* 刷新按钮 - 展开状态显示在右侧 */}
                  {!isCollapsed && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation(); // 阻止冒泡到外层按钮
                        handleRefreshUserInfo();
                      }}
                      className={`p-2 rounded-lg transition-all duration-300 ${
                        isRefreshing 
                          ? 'text-orange-500 animate-spin' 
                          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                      }`}
                      title={isRefreshing ? '刷新中...' : '刷新用户信息'}
                      disabled={isRefreshing}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* 收缩状态下的钻石数量 - 单独一行 */}
                {isCollapsed && (
                  <div className="mt-2 flex justify-center">
                    <div className="relative group">
                      <div className="px-2 py-1 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center gap-1">
                        <Gem className="w-3 h-3 text-white" />
                        <span className="text-xs text-white font-medium">{user.diamond_count || 0}</span>
                      </div>
                      {/* 悬浮提示 */}
                      <div className="absolute left-full ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50">
                        <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          钻石余额: {user.diamond_count || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* 收缩状态下的刷新按钮 - 单独一行 */}
                {isCollapsed && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // 阻止冒泡到外层按钮
                      handleRefreshUserInfo();
                    }}
                    className={`mt-2 w-full p-2 rounded-lg transition-all duration-300 flex justify-center ${
                      isRefreshing 
                        ? 'text-orange-500' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    title={isRefreshing ? '刷新中...' : '刷新用户信息'}
                    disabled={isRefreshing}
                  >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* 未登录状态的底部 */}
        {!isLoggedIn && (
          <div className="flex-shrink-0 bg-gradient-to-t from-gray-50 to-white border-t border-gray-200">
            <div className={`${isCollapsed ? 'p-2' : 'p-4'}`}>
              <button
                onClick={handleLoginRequired}
                className={`w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300 ${
                  isCollapsed ? 'p-2' : 'px-4 py-3'
                }`}
              >
                {isCollapsed ? (
                  <User className="w-4 h-4 mx-auto" />
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="text-sm">{t('sidebar.actions.loginRegister')}</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        )}
      </aside>

      {/* Coming Soon 提示 */}
      {showComingSoon && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-down">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 px-6 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">{comingSoonText}</p>
              <p className="text-sm text-gray-500">{t('sidebar.status.developingMessage')}</p>
            </div>
          </div>
        </div>
      )}

      {/* 登录弹窗 */}
      <LoginModal 
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        onLogin={handleLoginSuccess}
      />
    </>
  );
} 
